import { makeAutoObservable } from 'mobx';
import validateUrl from '../helpers/validate_url';
import DefaultCover from '../assets/default_cover.jpg';
import { getMainBook, saveMainBook, scrapeAmazon } from '../services/api';
import moment from 'moment';

class MainBookFormStore {
  formDataTemplate = {
    id: null,
    title: '',
    subtitle: '',
    authors: [],
    description: '',
    categories: [],
    language: '',
    pages_count: '',
    cover_url: '',
    use_default_cover: false,
    origin_url: '',
    reviews: [],
    empty_reviews: false,
    manual_input: false,
    links_amazon: {
      empty: false,
      isbn: '',
      asin: '',
      monitoring_url: '',
      physical_url: '',
      digital_url: ''
    },
    links_meli: {
      empty: false,
      isbn: '',
      monitoring_url: '',
      physical_url: '',
    }
  };
  formData = { ...this.formDataTemplate };
  loadedImage = null;
  errors = {
    id: false,
    title: false,
    subtitle: false,
    authors: false,
    description: false,
    categories: false,
    language: false,
    cover_url: false,
    origin_url: false,
    pages_count: false,
    publisher: false,
    release_date: false,
    reviews: false,
    reviewsFields: [],
    links_amazon: false,
    links_meli: false,
    linksAmazonFields: {},
    linksMeliFields: {}
  };
  validators = {
    id: () => this.setErrors({ ...this.errors, id: false }),
    title: () => this.setErrors({ ...this.errors, title: !this.formData.title?.length > 0 }),
    subtitle: () => this.setErrors({ ...this.errors, subtitle: false }),
    description: () => this.setErrors({ ...this.errors, description: !this.formData.description?.length > 0 }),
    exemplary_type: () => this.setErrors({ ...this.errors, exemplary_type: false }),
    buy_url: () => this.setErrors({ ...this.errors, buy_url: false }),
    authors: () => {
      if (this.formData.authors.length <= 0) {
        this.setErrors({ ...this.errors, authors: true });
        return;
      }

      for (let i = 0; i < this.formData.authors.length; i++) {
        const name = this.formData.authors[i].name;
        if (name === null || name.length < 3) {
          this.setErrors({ ...this.errors, authors: true });
          return;
        }
      }

      this.setErrors({ ...this.errors, authors: false });
    },
    categories: () => {
      if (this.formData.categories.length <= 0) {
        this.setErrors({ ...this.errors, categories: true });
        return;
      }

      for (let i = 0; i < this.formData.categories.length; i++) {
        if (this.formData.categories[i].length < 3) {
          this.setErrors({ ...this.errors, categories: true });
          return;
        }
      }

      this.setErrors({ ...this.errors, categories: false });
    },
    language: () => this.setErrors({ ...this.errors, language: !this.formData.language?.length > 0 }),
    cover_url: () => this.setErrors({ ...this.errors, cover_url: !validateUrl(this.formData.cover_url) }),
    origin_url: () => this.setErrors({ ...this.errors, origin_url: !validateUrl(this.formData.origin_url) }),
    loadedImage: () => this.setErrors({
      ...this.errors,
      loadedImage: this.loadedImage === null || !this.loadedImage.length > 3
    }),
    pages_count: () => {
      if (!isNaN(this.formData.pages_count) && isFinite(this.formData.pages_count) && this.formData.pages_count > 0) {
        this.setErrors({ ...this.errors, pages_count: false });
        return;
      }

      this.setErrors({ ...this.errors, pages_count: true });
    },
    publisher: () => this.setErrors({ ...this.errors, publisher: !this.formData.publisher?.length > 0 }),
    release_date: () => this.setErrors({ ...this.errors, release_date: this.formData.release_date?.length > 0 && isNaN(Date.parse(this.formData.release_date)) }),
    reviews: () => {
      if (this.showReviews === false) {
        this.setErrors({ ...this.errors, reviews: false, reviewsFields: [] });
        return;
      }

      if (this.formData.reviews.length <= 0) {
        this.setErrors({ ...this.errors, reviews: true });
        return;
      }

      let valid = true;
      const reviewsErrors = [];

      for (let i = 0; i < this.formData.reviews.length; i++) {
        reviewsErrors[i] = { author: false, content: false };

        if (this.formData.reviews[i].author < 1) {
          reviewsErrors[i].author = true;
          valid = false;
        }

        if (this.formData.reviews[i].content < 1) {
          reviewsErrors[i].content = true;
          valid = false;
        }
      }

      this.setErrors({ ...this.errors, reviews: !valid, reviewsFields: reviewsErrors });
    },
    linksAmazon: () => {
      if (this.showAmazonLinks === false) {
        this.setErrors({ ...this.errors, linksAmazon: false });
        return;
      }

      const isPhysicalUrlValid = validateUrl(this.formData.links_amazon.physical_url);
      const isIsbnValid = this.formData.links_amazon.isbn?.length === 14;
      const isDigitalUrlValid = validateUrl(this.formData.links_amazon.digital_url);
      const isAsinValid = this.formData.links_amazon.asin?.length === 10;

      const isPhysicValid = isPhysicalUrlValid && isIsbnValid;
      const isDigitalValid = isDigitalUrlValid && isAsinValid;

      this.setErrors({
        ...this.errors,
        linksAmazon: !(isPhysicValid || isDigitalValid),
        linksAmazonFields: {
          monitoring_url: !validateUrl(this.formData.links_amazon.monitoring_url),
          physical_url: !isPhysicalUrlValid,
          isbn: !isIsbnValid,
          digital_url: !isDigitalUrlValid,
          asin: !isAsinValid
        }
      });
    },
    linksMeli: () => {
      if (this.showMeliLinks === false) {
        this.setErrors({ ...this.errors, linksMeli: false });
        return;
      }

      const isPhysicalUrlValid = validateUrl(this.formData.links_meli.physical_url);
      const isIsbnValid = this.formData.links_meli.isbn?.length === 14;

      const isPhysicValid = isPhysicalUrlValid && isIsbnValid;

      this.setErrors({
        ...this.errors,
        linksMeli: !isPhysicValid,
        linksMeliFields: {
          monitoring_url: !validateUrl(this.formData.links_meli.monitoring_url),
          physical_url: !isPhysicalUrlValid,
          isbn: !isIsbnValid,
        }
      });
    }
  };
  reviewTemplate = {
    author: '',
    content: ''
  };
  activeTab = 0;
  tabsErrors = [true, true, true, true];
  showReviews = true;
  showAmazonLinks = true;
  showMeliLinks = true;
  loading = false;
  showFullForm = false;
  navigate = null;
  snackStore = null;

  constructor(userStore, snackStore, navigate) {
    makeAutoObservable(this);
    this.userStore = userStore;
    this.navigate = navigate;
    this.snackStore = snackStore;
  }

  setLoading(loading) {
    this.loading = loading;
  }

  setManualInput() {
    this.formData = { ...this.formDataTemplate, manual_input: true };
    this.setShowFullForm(true);
  }

  setShowFullForm(showFullForm) {
    this.showFullForm = showFullForm;
  }

  setFromAmazon(data) {

    data.release_date = moment(data.release_date, 'YYYY/MM/DD');

    this.formData = { ...this.formDataTemplate, ...data, use_default_cover: data?.cover_url === null };


    if (data?.cover_url) {
      this.setLoadedImage(data.cover_url);
    } else {
      this.setLoadedImage(DefaultCover);
    }
  }

  setErrors(errors) {
    this.errors = errors;

    const tabsValidation = [];

    const tabsValidators = [
      ['title', 'subtitle', 'authors', 'description', 'categories', 'language', 'pages_count', 'loadedImage', 'publisher', 'release_date'],
      ['reviews'],
      ['linksAmazon'],
      ['linksMeli']
    ];

    for (let i = 0; i < tabsValidators.length; i++) {
      tabsValidation[i] = tabsValidators[i].map(field => this.errors[field]).reduce((acc, curr) => acc || curr, false);
    }

    tabsValidation[4] = tabsValidation[0] || tabsValidation[1] || tabsValidation[2];
    this.setTabsErrors(tabsValidation);
  }

  setActiveTab(tab) {
    this.activeTab = tab;
  }

  setTabsErrors(errors) {
    this.tabsErrors = errors;
  }

  setShowReviews(show) {
    this.showReviews = show;
    this.setFormData('empty_reviews', !show);
    this.validateFields();
  }

  setShowAmazonLinks(show) {
    this.showAmazonLinks = show;
    this.formData.links_amazon = { ...this.formData.links_amazon, empty: !show };
    this.validateFields();
  }

  setShowMeliLinks(show) {
    this.showMeliLinks = show;
    this.formData.links_meli = { ...this.formData.links_meli, empty: !show };
    this.validateFields();
  }

  setFormData(field, value) {
    const m = field.match(/^(.+)\[(\d+)\]$/);
    if (m) {
      this.formData[m[1]][Number(m[2])] = value;
      value = this.formData[m[1]];
    }

    this.formData = {
      ...this.formData,
      [field]: value
    };
    this.validateFields();
  }

  setAmazonLinkData(field, value) {
    const link = { ...this.formData.links_amazon };
    link[field] = value;
    link['seller_id'] = 1;
    this.setFormData('links_amazon', link);
  }

  setMeliLinkData(field, value) {
    const link = { ...this.formData.links_meli };
    link[field] = value;
    link['seller_id'] = 2;
    this.setFormData('links_meli', link);
  }

  addReview() {
    this.setFormData('reviews', [...this.formData.reviews, { ...this.reviewTemplate }]);
  }

  removeReview(index) {
    const reviews = [...this.formData.reviews];
    reviews.splice(index, 1);
    this.setFormData('reviews', reviews);
  }

  handleChangeReview(index, field, value) {
    const newReviews = [...this.formData.reviews];
    newReviews[index][field] = value;
    this.setFormData('reviews', newReviews);
  }

  setLoadedImage(loadedImage) {
    this.loadedImage = loadedImage ?? DefaultCover;
    this.formData = {
      ...this.formData,
      cover_url: loadedImage,
      use_default_cover: loadedImage === null
    };
    this.validateFields();
  }

  validateFields = () => {
    for (const key in this.validators) {
      this.validators[key]();
    }
  };

  hasErrors = () => {
    for (const key in this.errors) {
      if (this.errors[key] === true) {
        return true;
      }
    }

    return false;
  };

  loadMainBook = async (id) => {
    this.setLoading(true);
    try {
      const response = await getMainBook(id, this.userStore.token);
      this.formData = {
        ...this.formDataTemplate,
        ...response.data.data
      };

      this.loadLinks(response.data.data);

      if (response.data.data.reviews?.length <= 0) {
        this.showReviews = false;
        this.formData = { ...this.formData, empty_reviews: true };
      }

      this.setLoadedImage(this.formData.cover_url);
      this.setLoading(false);
      this.setShowFullForm(true);
    } catch (e) {
      this.setLoading(false);
      this.snackStore.showErrorSnack('Erro ao carregar livro');
      this.navigate('/app/main/books');
    }
  }

  loadLinks = (response) => {
    if (response.links == null || response.links.length === 0) {
      this.setShowAmazonLinks(false);
    }

    const found = [];

    response.links.forEach(link => {
      link['isbn'] = link.isbn?.length === 13 ? `${link.isbn.slice(0, 3)}-${link.isbn.slice(3)}` : link.isbn;
      found.push(link['seller_id']);

      if (link['seller_id'] === 1) {

        this.formData = { ...this.formData, links_amazon: { ...this.formData.links_amazon, ...link, empty: false } };
      }
      if (link['seller_id'] === 2) {
        this.formData = { ...this.formData, links_meli: { ...this.formData.links_meli, ...link, empty: false } };
      }
    });

    if (!found.includes(1)) {
      this.setShowAmazonLinks(false);
    }

    if (!found.includes(2)) {
      this.setShowMeliLinks(false);
    }

  }

  searchInAmazon = async (url) => {
    if (this.loading) {
      return;
    }
    this.setLoading(true);
    try {
      const result = await scrapeAmazon(url, this.userStore.token);
      this.setFromAmazon(result.data.data);
      console.log(result.data.data);
      this.setLoading(false);
      this.setShowFullForm(true);
      this.setAmazonLinkData('monitoring_url', url);
      this.setAmazonLinkData('isbn', result.data.data.isbn13);
    } catch (e) {
      this.setLoading(false);
    }
  }

  saveBook = async () => {
    if (this.loading) {
      return;
    }

    this.setLoading(true);

    const finalData = { ...this.formData, pages_count: parseInt(this.formData.pages_count) };

    const links = [];

    if (this.formData.links_amazon.empty === false) {
      links.push({ ...this.formData.links_amazon });
    }
    if (this.formData.links_meli.empty === false) {
      links.push({ ...this.formData.links_meli });
    }
    finalData.links = links;

    try {
      await saveMainBook(finalData, this.userStore.token);
      this.setLoading(false);
      this.snackStore.showSuccessSnack(`Livro-Pai "${finalData.title}" salvo com sucesso`);
      this.navigate(`/app/main/books`);
    } catch (e) {
      const messages = [];
      const errors = e.response?.data?.error?.fields ?? {};
      for (const key in errors) {
        messages.push(errors[key].join('\n'));
      }
      this.setLoading(false);
      this.snackStore.showErrorSnack(messages.length > 0 ? messages.join('\n') : 'Erro ao salvar livro');
    }
  }
}

export default MainBookFormStore;
